// xadomTableRowSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Cell, Column, Row, RowData, RowsState, ValueType } from '../types';
import {
    createEmptyRow,
    createInitialRows,
    prepareAddRow,
    prepareAddRows,
    prepareSetRow,
    prepareSetRows,
    rowsAdapter,
    sortRowsLogic
} from './logic';

// Adapter는 logic.ts에서 import

interface SortAction {
    tableId: string;
    columnId: string;
    orderType: string | undefined;
    valueType: ValueType;
}

// Initial state
const initialState: RowsState = {};

// Slice to manage rows per table
const xadomTableRowSlice = createSlice({
    name: 'xadomTableRow',
    initialState,
    reducers: {
        initializeRows: (
            state,
            action: PayloadAction<{
                tableId: string;
                initialRows?: RowData[];
            }>
        ) => {
            const { tableId, initialRows } = action.payload;

            if (!state[tableId]) {
                state[tableId] = rowsAdapter.getInitialState();
            }

            if (initialRows && initialRows.length > 0) {
                const rowsWithIds = createInitialRows(initialRows);
                rowsAdapter.addMany(state[tableId], rowsWithIds);
            }
        },
        addRow: {
            reducer: (state, action: PayloadAction<{ tableId: string; row: Row }>) => {
                const { tableId, row } = action.payload;
                if (!state[tableId]) {
                    state[tableId] = rowsAdapter.getInitialState();
                }
                rowsAdapter.addOne(state[tableId], row);
            },
            prepare: prepareAddRow
        },
        addRows: {
            reducer: (state, action: PayloadAction<{ tableId: string; rows: Row[] }>) => {
                const { tableId, rows } = action.payload;
                rowsAdapter.addMany(state[tableId], rows);
            },
            prepare: prepareAddRows
        },
        addEmptyRow: (
            state,
            action: PayloadAction<{ tableId: string, columns: { [columnId: string]: Column } }>
        ) => {
            const { tableId, columns } = action.payload;

            if (state[tableId]) {
                const emptyRow = createEmptyRow(columns);
                rowsAdapter.addOne(state[tableId], emptyRow);
            }
        },
        updateRows: (state, action: PayloadAction<{ tableId: string, rows: Row[] }>) => {
            const { tableId, rows } = action.payload;
            if (state[tableId]) {
                rowsAdapter.updateMany(
                    state[tableId],
                    rows.map(row => ({ id: row.id, changes: row }))
                );
            }
        },
        setRows: {
            reducer: (state, action: PayloadAction<{ tableId: string; rows: Row[] }>) => {
                const { tableId, rows } = action.payload;
                if (!state[tableId]) return;
                rowsAdapter.setAll(state[tableId], rows);
            },
            prepare: prepareSetRows
        },
        setRow: {
            reducer: (state, action: PayloadAction<{ tableId: string; rowId: string; row: Row }>) => {
                const { tableId, rowId, row } = action.payload;
                if (state[tableId]) {
                    rowsAdapter.updateOne(state[tableId], { id: rowId, changes: row });
                }
            },
            prepare: prepareSetRow
        },
        setCell: (state, action: PayloadAction<{ tableId: string; rowId: string; columnId: string; cell: Cell }>) => {
            const { tableId, rowId, columnId, cell } = action.payload;
            if (state[tableId]) {
                const row = rowsAdapter.getSelectors().selectById(state[tableId], rowId);
                if (row) {
                    const updatedData = { ...row.data, [columnId]: cell };
                    rowsAdapter.updateOne(state[tableId], { id: rowId, changes: { data: updatedData } });
                }
            }
        },
        updateRow: (state, action: PayloadAction<{ tableId: string; id: string; changes: Partial<Row> }>) => {
            const { tableId, id, changes } = action.payload;
            if (state[tableId]) {
                rowsAdapter.updateOne(state[tableId], { id, changes });
            }
        },
        updateCell: (state, action) => {
            const { tableId, rowId, columnId, cell } = action.payload;
            const tableState = state[tableId];

            if (!tableState) {
                throw new Error(`테이블이 존재하지 않습니다. (tableId: ${tableId})`);
            }

            if (!tableState.entities[rowId]) {
                throw new Error(`행이 존재하지 않습니다. (rowId: ${rowId})`);
            }

            const targetRow = tableState.entities[rowId];
            targetRow.data[columnId] = cell;
        },
        removeRow: (state, action: PayloadAction<{ tableId: string; rowId: string }>) => {
            const { tableId, rowId } = action.payload;
            if (state[tableId]) {
                rowsAdapter.removeOne(state[tableId], rowId);
            }
        },
        clearRows: (state, action: PayloadAction<{ tableId: string }>) => {
            const { tableId } = action.payload;
            if (state[tableId]) {
                rowsAdapter.removeAll(state[tableId]);
            }
        },
        disposeTable: (state, action: PayloadAction<{ tableId: string }>) => {
            const { tableId } = action.payload;
            // 특정 테이블의 행 데이터 완전 제거
            if (state[tableId]) {
                delete state[tableId];
            }
        },
        sortRows: (state, action: PayloadAction<SortAction>) => {
            const { tableId, columnId, orderType, valueType } = action.payload;
            if (state[tableId] && orderType) {
                const allRows = [...rowsAdapter.getSelectors().selectAll(state[tableId])];
                const sortedRows = sortRowsLogic(allRows, columnId, orderType, valueType);

                rowsAdapter.removeAll(state[tableId]);
                rowsAdapter.setAll(state[tableId], sortedRows);
            }
        },
    },

});

export const {
    initializeRows,
    addRow,
    addRows,
    addEmptyRow,
    sortRows,
    updateRows,
    setRows,
    updateRow,
    updateCell,
    removeRow,
    clearRows,
    setRow,
    disposeTable
} = xadomTableRowSlice.actions;

export default xadomTableRowSlice;